import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Section, PhoneInput, isValidPhone, toPhoneE164, CoordinateInput, isValidCoordinate, MultiSelect } from "../components/UI";
import { useAuth } from "../lib/AuthContext";
import LocationPicker from "../components/LocationPicker";
import { DokumenUploader } from "../components/DokumenSupplier";
import { KAB_KOTA, KECAMATAN_MAP } from "../lib/wilayah";

export default function ProfilSupplierPage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    nama_usaha: "", jenis_produk: [], alamat: "", no_hp: "", kota: "Medan", kecamatan: "", kelurahan: "", nama_pic: "", lat: 3.5952, lng: 98.6722,
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [jenisOptions, setJenisOptions] = useState([]);

  const muatProfil = async () => {
    const { data } = await api.get("/suppliers/me/profile");
    if (data) setProfile(data);
  };

  useEffect(() => {
    (async () => {
      const { data: master } = await api.get("/master-data/jenis-produk");
      setJenisOptions(master.map((m) => m.nama));
      const { data } = await api.get("/suppliers/me/profile");
      if (data) {
        setProfile(data);
        setForm({
          nama_usaha: data.nama_usaha, jenis_produk: data.jenis_produk || [],
          alamat: data.alamat, no_hp: toPhoneE164(data.no_hp), kota: data.kota,
          kecamatan: data.kecamatan || "", kelurahan: data.kelurahan || "",
          nama_pic: data.nama_pic || "",
          lat: data.lat, lng: data.lng,
        });
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });
    if (form.jenis_produk.length === 0) {
      setMsg({ type: "error", text: "Pilih minimal satu jenis produk." });
      return;
    }
    if (!isValidPhone(form.no_hp)) {
      setMsg({ type: "error", text: "No. HP tidak valid. Masukkan minimal 8 angka setelah +62." });
      return;
    }
    if (!isValidCoordinate(form.lat, "lat") || !isValidCoordinate(form.lng, "lng")) {
      setMsg({ type: "error", text: "Koordinat tidak valid. Latitude -90 s/d 90, longitude -180 s/d 180." });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, lat: Number(form.lat), lng: Number(form.lng) };
      if (profile) await api.patch(`/suppliers/${profile.id}`, payload);
      else {
        payload.verified = false; payload.rating = 0;
        const { data } = await api.post("/suppliers", payload);
        setProfile(data);
      }
      setMsg({ type: "success", text: "Profil usaha berhasil disimpan." });
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e.response?.data?.detail) || e.message });
    } finally { setSaving(false); }
  };

  const onKotaChange = (kota) => {
    const c = KAB_KOTA.find((c) => c[0] === kota);
    const kecs = KECAMATAN_MAP[kota] ? Object.keys(KECAMATAN_MAP[kota]) : [];
    const defaultKec = kecs[0] || "";
    const defaultKel = defaultKec && KECAMATAN_MAP[kota][defaultKec] ? KECAMATAN_MAP[kota][defaultKec][0] : "";
    setForm((f) => ({
      ...f,
      kota,
      kecamatan: defaultKec,
      kelurahan: defaultKel,
      lat: c ? c[1] : f.lat,
      lng: c ? c[2] : f.lng
    }));
  };

  const onKecamatanChange = (kec) => {
    const kels = KECAMATAN_MAP[form.kota]?.[kec] || [];
    const defaultKel = kels[0] || "";
    setForm((f) => ({
      ...f,
      kecamatan: kec,
      kelurahan: defaultKel
    }));
  };

  const onKelurahanChange = (kel) => {
    setForm((f) => ({ ...f, kelurahan: kel }));
  };

  const kecamatanOptions = KECAMATAN_MAP[form.kota] ? Object.keys(KECAMATAN_MAP[form.kota]) : [];
  const kelurahanOptions = form.kecamatan && KECAMATAN_MAP[form.kota]?.[form.kecamatan]
    ? KECAMATAN_MAP[form.kota][form.kecamatan]
    : [];

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profil-supplier-page">
      <header>
        <div className="kedan-label text-slate-500">Akun Supplier</div>
        <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">Profil Usaha</h1>
        <p className="text-slate-500 text-sm mt-1">Informasi usaha yang ditampilkan kepada SPPG dan KPPG.</p>
      </header>

      <form onSubmit={submit} className="kedan-card p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-sm font-medium text-slate-700 mb-1 block">Nama Usaha</label>
            <input className="kedan-input" required value={form.nama_usaha} onChange={(e) => setForm({ ...form, nama_usaha: e.target.value })} data-testid="profil-nama" />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm font-medium text-slate-700 mb-1 block">Nama PIC</label>
            <input className="kedan-input" value={form.nama_pic} onChange={(e) => setForm({ ...form, nama_pic: e.target.value })} placeholder="Person In Charge" data-testid="profil-nama-pic" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Jenis Produk</label>
            <MultiSelect
              options={jenisOptions}
              value={form.jenis_produk}
              onChange={(v) => setForm({ ...form, jenis_produk: v })}
              placeholder="Pilih satu atau lebih jenis produk"
              emptyText="Master jenis produk masih kosong. Hubungi admin."
              testid="profil-jenis"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">No. HP</label>
            <PhoneInput required value={form.no_hp} onChange={(v) => setForm({ ...form, no_hp: v })} testid="profil-hp" />
            <p className="text-xs text-slate-400 mt-1">Hanya angka, tanpa 0 di depan. Disimpan sebagai +62…</p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Kabupaten/Kota</label>
            <select className="kedan-input" value={form.kota} onChange={(e) => onKotaChange(e.target.value)} data-testid="profil-kota">
              {KAB_KOTA.map(([c]) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Kecamatan</label>
            <select className="kedan-input" value={form.kecamatan} onChange={(e) => onKecamatanChange(e.target.value)} data-testid="profil-kecamatan" required>
              <option value="">Pilih Kecamatan</option>
              {kecamatanOptions.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Kelurahan/Desa</label>
            <select className="kedan-input" value={form.kelurahan} onChange={(e) => onKelurahanChange(e.target.value)} data-testid="profil-kelurahan" required>
              <option value="">Pilih Kelurahan/Desa</option>
              {kelurahanOptions.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-sm font-medium text-slate-700 mb-1 block">Titik Lokasi Usaha</label>
            <LocationPicker
              lat={form.lat}
              lng={form.lng}
              onChange={(lat, lng) => setForm((f) => ({ ...f, lat, lng }))}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Latitude</label>
            <CoordinateInput axis="lat" value={form.lat} onChange={(v) => setForm({ ...form, lat: v })} testid="profil-lat" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Longitude</label>
            <CoordinateInput axis="lng" value={form.lng} onChange={(v) => setForm({ ...form, lng: v })} testid="profil-lng" />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm font-medium text-slate-700 mb-1 block">Alamat Lengkap</label>
            <textarea rows="2" className="kedan-input" required value={form.alamat} onChange={(e) => setForm({ ...form, alamat: e.target.value })} data-testid="profil-alamat" />
          </div>
        </div>

        {msg.text && (
          <div className={`text-sm rounded-md px-3 py-2 ${
            msg.type === "success" ? "bg-emerald-50 border border-emerald-200 text-emerald-800" :
            "bg-red-50 border border-red-200 text-red-700"
          }`} data-testid="profil-msg">{msg.text}</div>
        )}

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="kedan-btn-primary" data-testid="profil-save">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan..." : "Simpan Profil"}
          </button>
        </div>
      </form>

      <div className="kedan-card p-6 space-y-4" data-testid="dokumen-verifikasi-card">
        <div>
          <h2 className="font-display font-semibold text-slate-900">Dokumen Verifikasi</h2>
          <p className="text-sm text-slate-500 mt-1">
            Dokumen ini menjadi data dukung bagi KPPG/Pemda saat memverifikasi usaha Anda.
          </p>
        </div>
        {profile ? (
          <DokumenUploader supplierId={profile.id} dokumen={profile.dokumen} onChanged={muatProfil} />
        ) : (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            Simpan profil usaha terlebih dahulu sebelum mengunggah dokumen.
          </p>
        )}
      </div>
    </div>
  );
}
